<template>
<!-- TODO: -->
<!-- HISTORY:
   V260423.1: Where appropriate, replaced this.model with this.query.type to support Auto Detect.
   V250903.1: In the Result panel, replaced v-show with v-if because collapsing the panel caused all three <v-expansion-panel-content> sections to appear.
   V250902.1: 1st release (copied from LsQuery V250811).
-->
<v-container fluid class="px-3 py-3">
   <v-card class="pb-2">
      <v-card-title>
         <h1 class="title font-weight-bold grey--text darken-4 pl-2" style="color:#757575 !important">
            <v-icon class="pr-1">auto_fix_high</v-icon><!-- smart_toy | auto_awesome -->
            <span>AI Assistant</span>
         </h1>
      </v-card-title>

      <v-card-text class="pb-2">
         <v-expansion-panels focusable
            v-model="panelQuery"
         >
            <v-expansion-panel>
               <v-expansion-panel-header>
                  <strong>Model Builder</strong>
               </v-expansion-panel-header>
               <v-expansion-panel-content :class="`pt-4 ${model ? '' : 'pb-2'}`">
                  <v-card flat>
                     <v-form lazy-validation
                        ref="mainForm"
                        v-model="isMainFormValid"
                     >
                        <v-row>
                           <v-col xs="12" sm="12" md="3" class="py-2">
                              <v-select dense persistent-hint hide-selected required
                                 ref="model"
                                 placeholder="select a model..."
                                 hint="* Model"
                                 :items="modelItems"
                                 v-model="model"
                                 @change="modelChanged"
                              ></v-select>
                           </v-col>
                        </v-row>
                        <v-row v-if="model">
                           <v-col cols="12" class="pt-0 pb-0">
                              <v-textarea outlined
                                 ref="prompt"
                                 class="pt-3 pr-0 mb-0"
                                 label="* Prompt"
                                 placeholder="type your prompt..."
                                 rows="2"
                                 :rules="[rules.required]"
                                 v-model="prompt"
                                 @keyup="promptChanged"
                              ></v-textarea>
                           </v-col>
                        </v-row>
                        <v-row v-if="error">
                           <v-col cols="12" class="py-0">
                              <v-alert dense outlined
                                 type="error"
                              >{{error}}</v-alert>
                           </v-col>
                        </v-row>
                     </v-form>
                     <v-card-actions v-if="model" class="px-0 py-0">
                        <div class="flex-grow-1"></div>
                        <v-btn text
                           color="blue darken-1"
                           :disabled="loadingQuery || isSubmitDisabled || !isMainFormValid"
                           :loading="loadingQuery"
                           @click="submitQuery"
                        >Submit Query</v-btn>
                     </v-card-actions>
                  </v-card>
               </v-expansion-panel-content>
            </v-expansion-panel>
         </v-expansion-panels>
      </v-card-text>

      <v-card-text v-if="!isEmpty(query)" class="pt-2">
         <v-expansion-panels focusable
            v-model="panelModel"
         >
            <v-expansion-panel>
               <v-expansion-panel-header>
                  <strong>Model</strong>
               </v-expansion-panel-header>
               <v-expansion-panel-content class="pt-5">
                  <v-row class="py-0">
                     <v-col cols="10" class="pt-1 pr-0 pb-4">
                        <pre v-if="isModelExpanded">{{JSON.stringify(query, null, 3)}}</pre>
                        <span v-else>{{JSON.stringify(query, null, 1)}}</span>
                     </v-col>
                     <v-col cols="2" class="px-0 py-0 d-flex justify-end">
                        <v-btn text xs-small
                           color="blue darken-1"
                           @click="isModelExpanded = !isModelExpanded"
                        >{{isModelExpanded ? 'Collapse' : 'Expand'}}
                           <v-icon v-if="isModelExpanded" right dark>chevron_right</v-icon>
                           <v-icon v-else right dark>expand_more</v-icon>
                        </v-btn>
                     </v-col>
                  </v-row>
               </v-expansion-panel-content>
            </v-expansion-panel>
         </v-expansion-panels>
      </v-card-text>

      <v-card-text v-if="!isEmpty(query)" class="pt-0">
         <v-expansion-panels focusable
            v-model="panelResult"
         >
            <v-expansion-panel>
               <v-expansion-panel-header>
                  <strong>Result</strong>
               </v-expansion-panel-header>
               <v-expansion-panel-content class="pt-2 pb-0">
                  <v-row class="pb-3">
                     <v-spacer></v-spacer>
                     <v-col cols="3" class="pt-5 pr-0 d-flex justify-end">
                        <v-btn v-if="resultType === 'Table'"
                           small
                           class="mr-3"
                           @click="exportDataToCsvClicked"
                        >Export Data to CSV
                           <v-icon right dark>file_upload</v-icon>
                        </v-btn>
                     </v-col>
                     <v-col cols="2" class="pt-2 pb-0 pl-0">
                        <v-select dense persistent-hint hide-selected required
                           ref="resultType"
                           placeholder="select a result type..."
                           hint="* Result Type"
                           :disabled="resultTypeItems.length === 1"
                           :items="resultTypeItems"
                           v-model="resultType"
                           @change="resultTypeChanged"
                        ></v-select>
                     </v-col>
                  </v-row>
               </v-expansion-panel-content>
               <v-expansion-panel-content v-if="isTable" class="pt-0 pb-1">
                  <v-data-table dense fixed-header
                     class="elevation-1"
                     :footer-props="{
                        itemsPerPageOptions: [5, 10, 20, -1],
                        showFirstLastPage: true
                     }"
                     :headers="queryResultHeaders"
                     :hide-default-footer="queryResult.length <= 5"
                     :items="queryResult"
                     :items-per-page="10"
                     :loading-text="$t('loading-text')"
                     :no-data-text="$t('no-data-text', { value: 'results' })"
                     :no-results-text="$t('no-results-text', { value: 'results' })"
                  >
                     <template v-slot:item="{item}">
                        <tr>
                           <td v-for="header in queryResultHeaders" :key="header.value">
                              {{ formatNumber(item[header.value], header.value) }}
                           </td>
                        </tr>
                     </template>
                  </v-data-table>
               </v-expansion-panel-content>
               <v-expansion-panel-content v-if="isChart" class="pt-0 pb-1">
                     <!-- :events="chartEvents" resultType-->
                  <GChart
                     ref="gChart"
                     :data="chartData"
                     :type="chartType"
                     :options="chartOptions"
                     :settings="gChartSettings"
                     :key="resultType"
                  />
                  <v-row v-show="false">
                     <v-spacer></v-spacer>
                     <v-col cols="2" class="pl-0 pr-3 pt-0 pb-2 d-flex justify-end">
                        <v-switch dense hide-details
                           class="mb-0"
                           label="World Map"
                           v-model="isRegionChecked"
                        ></v-switch>
                     </v-col>
                  </v-row>
               </v-expansion-panel-content>
               <v-expansion-panel-content v-if="isKpi" class="pt-3 pb-1">
                  <v-card outlined class="pa-3">
                     <v-row dense
                        v-for="(value, key) in queryResult[0]"
                        :key="key"
                        class="body-1 font-weight-bold"
                     >
                        <v-col cols="12"><span class="grey--text text-darken-1">{{ key }}: </span>{{ formatNumber(value) }}</v-col>
                     </v-row>
                  </v-card>
                 </v-expansion-panel-content>
            </v-expansion-panel>
         </v-expansion-panels>
      </v-card-text>
   </v-card>
</v-container>
</template>

<script>
import { GChart } from 'vue-google-charts';
import { APIService } from '../services/ls-api-service.js';
import { isEmpty, formatNumber, exportDataToCSV } from '../mixins/bt-mixin.js';

const NAME = "LsAiAssistant";
const MODELS = {
   metrics: 'metrics',
   dims: 'dims-metrics',
   dimsDim: 'dims-dim',
   date: 'date-metrics',
   dateDate: 'date-date',
   dateDim: 'date-dim',
   histogram: 'histogram-metrics',
   rows: 'rows'
}
const GEO_HIERARCHY = [
   ["country", "countries"],
   ["state", "states", "province", "provinces"],
   ["city", "cities", "town", "towns"]
];

function _isValidGeoHierarchy(geoFields) {
   const normalizedGeoFields = geoFields.map(f => f.toLowerCase());

  // Map each geo field to its hierarchy level
   const levels = normalizedGeoFields.map(field => {
      for (let i = 0; i < GEO_HIERARCHY.length; i++) {
         if (GEO_HIERARCHY[i].some(h => h === field)) {
            return i;
         }
      }

      return -1; // invalid field
   });

   // Check if all fields are recognized
   if (levels.includes(-1)) return false;

   // Check if levels are in ascending order (i.e., proper hierarchy)
   for (let i = 1; i < levels.length; i++) {
      if (levels[i] < levels[i - 1]) return false;
   }

   return true;
}

export default {
   name: NAME,

   components: {
      GChart
   },

   props: {
      debug: {
         type: Boolean,
         default: false
      },

      isActualEndpoint: {
         type: Boolean,
         default: true
      }
   },

   data() {
      return {
         rules: {
            required: value => !!value || "Value is required!"
         },
         modelItems: [
            { text: 'Auto Detect', value: 'auto' },
            { text: 'Metrics', value: 'metrics' },
            { text: 'Dimensions - Metrics', value: 'dims-metrics' },
            { text: 'Dimensions - Dimension', value: 'dims-dim' },
            { text: 'Date - Metrics', value: 'date-metrics' },
            { text: 'Date - Date', value: 'date-date' },
            { text: 'Date - Dimension', value: 'date-dim' },
            { text: 'Histogram - Metrics', value: 'histogram-metrics' },
            { text: 'Rows', value: 'rows' }
         ],
         model: 'auto',
         prompt: '',
         isMainFormValid: false,
         apiService: null,
         panelQuery: 0,
         panelModel: 0,
         panelResult: 0,
         error: '',
         query: {},
         loadingQuery: false,
         queryResult: [],
         queryResultHeaders: [],
         isModelExpanded: false,
         isSubmitDisabled: true,
         pivotDimInItems: [],
         chartData: [],
         gChartSettings: {
            packages: ['corechart', 'table', 'map', 'geochart'],
            mapsApiKey: process.env.VUE_APP_GOOGLE_API_KEY
         },
         isTable: true,
         isKpi: false,
         resultTypeItems: [],
         resultType: 'Table',
         chartType: 'LineChart',
         isRegionChecked: true
      }
   },

   computed: {
      token() {
         return this.$store.getters.token;
      },

      chartOptions() {
         const options = {
            height: "600", //400, 250
            legend: {
               position: "top"
            }
         };

         if (this.resultType === "DonutChart") {
            options.pieHole = "0.3";
         }

         if (this.resultType === "GeoChart") {
            options.displayMode = 'markers';    // this enables bubbles
            options.colorAxis = { colors: ['#666666','#000000'] };
            options.region = this.isRegionChecked ? 'world' : 'US';  // or 'US', 'AU', etc. depending on your focus
            // options.resolution = this.geoChartResolution;
         } else {
            options.chartArea = {
               left: "150", //70
               right: "50",   //20
               bottom: 100
            };
         }

         return options;

         // hAxis: {
         //    slantedText: true, // makes labels slanted (easier to read)
         //    slantedTextAngle: 45, // or 90 for vertical
         //    textStyle: {
         //       fontSize: 12,
         //       color: '#333',
         //    },
         //    maxAlternation: 1, // prevents skipping labels
         // },
         // chartArea: {
         //    left: 50,
         //    right: 20,
         //    bottom: 80, // more space at the bottom
         //    top: 20,
         // }
      },

      isChart() {
         return !this.isTable && !this.isKpi;
      },

      isLocal() {
         return window.location.href.includes('http://localhost:8181/');
      },

      // initialPrompt() {
      //    if (!this.isLocal) return undefined;

      //    let prompt;
      //    switch (this.model) {
      //       case MODELS.metrics:
      //          prompt = '';
      //          break;
      //       case MODELS.dims:
      //          prompt = '';
      //          break;
      //       case MODELS.dimsDim:
      //          prompt = '';
      //          break;
      //       case MODELS.date:
      //       case MODELS.dateDate:
      //          // prompt = 'show me unique records by month and quarter on account creation date for the amount less than 10000';
      //          prompt = 'show me total by months and days';
      //          break;
      //       case MODELS.dateDim:
      //          prompt = '';
      //          break;
      //       case MODELS.histogram:
      //          prompt = '';
      //          break;
      //       case 'rows':
      //          return {
      //             columns: [ "Product", "price", "Payment_Type", "transaction_date"]
      //          };
      //       default:
      //          prompt = '';
      //          break;
      //    }

      //    return prompt;
      // }
   },

   watch: {
      token() {
         this.init();
      }
   },

   methods: {
      isEmpty,

      log(msg) {
         if (this.debug)
            console.log(`-----${NAME} V260423.1 says => ${msg}`);
      },

      logout() {
         this.$router.push('/');
      },

      async init() {
         if (this.token) {
            this.apiService = new APIService(this.jwt, this.token, this.debug, this.isActualEndpoint);
         }
      },

      modelChanged() {
         this.isSubmitDisabled = false;
         // if (this.isLocal) {
            setTimeout(() => {
               // this.prompt = this.initialPrompt;
               // this.submitQuery();
               this.$refs?.prompt?.focus();
            }, 100);
         // }
      },

      promptChanged() {
         this.isSubmitDisabled = false;
      },

      async submitQuery() {
         this.loadingQuery = true;
         this.error = '';
         this.query = {};
         this.queryResult = [];
         this.queryResultHeaders = [];
         this.chartData = [];

         const aiQuery = { prompt: this.prompt };
         let result = await this.apiService.getAiQueryResult(this.model, aiQuery);
         if (result.logout)
            this.logout();
         else if (result.message) {
            this.error = result.message;
         } else {
            //V260423: Replaced this.model with this.query.type
            this.query = result.data.model;
            const { type } = this.query;
            if (result.data.data?.length) {
               const data = result.data.data;
               const labels = this.query.metrics.map(m => m.label);
               let orderedQueryKeys;
               if (type === MODELS.dateDate) {
                  this.queryResult = this.reorderDateDateResult(data);
                  orderedQueryKeys = Object.keys(this.queryResult[0]);
               } else {
                  this.queryResult = data;
                  switch (type) {
                     case MODELS.metrics:
                        orderedQueryKeys = labels;
                        break;
                     case MODELS.dims:
                        orderedQueryKeys = [
                           ...this.getDims(),
                           ...labels
                        ];
                        break;
                     case MODELS.dimsDim:
                        orderedQueryKeys = [...this.query.groupByDims, ...this.query.pivotDimIn];
                        break;
                     case MODELS.date:
                        orderedQueryKeys = [...this.query.dateParts, ...labels];
                        break;
                     case MODELS.dateDim:
                        orderedQueryKeys = [...this.query.dateParts, ...this.query.pivotDimIn];
                        break;
                     default:
                        orderedQueryKeys = Object.keys(data[0]);
                        break;
                  }
               }
         
               this.queryResultHeaders = orderedQueryKeys.map(key => ({
                  text: key,
                  value: key,
                  align: 'left',
                  sortable: true
               }));

               this.getResultTypeItems();
               this.resultTypeChanged();
            }
         }

         this.log('queryResultHeaders=' + JSON.stringify(this.queryResultHeaders))

         this.loadingQuery = false;
         this.isSubmitDisabled = true;
      },

      reorderDateDateResult(data) {
         const prefix = this.query.dateParts[1] + ' ';
         const renamedData = data.map(obj => {
            const newObj = {};
            Object.entries(obj).forEach(([key, value]) => {
               if (!isNaN(key)) {
                  newObj[`${prefix}${key}`] = value;
               } else {
                  newObj[key] = value;
               }
            });

            return newObj;
         });

         const result = [];
         const keys = Object.keys(renamedData[0]);
         const lastKey = keys[keys.length - 1];
         JSON.parse(JSON.stringify(renamedData)).forEach(item => {
            const orderedMap = new Map();
            orderedMap.set(lastKey, item[lastKey]);
            for (let i = 0; i < keys.length - 1; i++) {
               orderedMap.set(keys[i], item[keys[i]]);
            }
            result.push(Object.fromEntries(orderedMap));
         });

         // console.warn('in reorderDateDateResult(): result=' + JSON.stringify(result));
         return JSON.parse(JSON.stringify(result));
      },

      formatNumber(val, key) {
         if (typeof val === 'string' || ['year', 'quarter', 'month', 'day', 'hour'].includes(key))
            return val;
         else
            return formatNumber(val, 2);
      },

      //V260423: Replaced this.model with this.query.type
      getResultTypeItems() {
         const { type } = this.query;
         this.resultTypeItems = [
            { text: 'Table', value: 'Table' },
         ];

         if (type === 'metrics') {
            this.resultTypeItems.push ({ text: 'KPI', value: 'KPI' });
         } else if (type != 'rows') {
            this.resultTypeItems.push (...[
               { text: 'Area Chart', value: 'AreaChart' },
               { text: 'Bar Chart', value: 'BarChart' },
               { text: 'Column Chart', value: 'ColumnChart' },
               { text: 'Line Chart', value: 'LineChart' }
            ]);

            if (this.query.metrics?.length === 1) {
               if (
                  type.endsWith('-metrics') ||
                  (type.endsWith('-dim') && this.query.pivotDimIn.length === 1)
               ) {
                  this.resultTypeItems.push (...[
                     { text: 'Pie Chart', value: 'PieChart' },
                     { text: 'Donut Chart', value: 'DonutChart' }
                  ]);
               }

               if (type.startsWith('dims-')) {
                  const dimKeys = this.query.dims || this.query.groupByDims;
                  const metricKeys = this.query.pivotDimIn || this.query.metrics.map(m => m.label);
                  if (metricKeys.length === 1 && _isValidGeoHierarchy(dimKeys)) {
                     this.resultTypeItems.push (
                        { text: 'Geo Chart', value: 'GeoChart'}
                     );
                  }
               }
            }
         }

         const currResultType = this.resultTypeItems.find(item => item.value === this.resultType);

         if (!this.resultType || !currResultType) {
            this.resultType = this.resultTypeItems[0].value;
            this.isTable = true;
            this.isKpi = false;
         }

         // { text: 'Combo Chart', value: 'ComboChart' },
      },

      resultTypeChanged() {
         this.isTable = this.resultType === 'Table';
         this.isKpi =  this.resultType === 'KPI';
         if (this.isChart) {
            setTimeout(() => {
               this.chartType = this.resultType.replace('Donut', 'Pie');
            }, 5);

            if (!this.chartData.length && this.queryResult.length)
               this.prepareChartData();
         }
      },

      //V260423: Replaced this.model with this.query.type
      async prepareChartData() {
         let dimKeys, metricKeys;
         switch (this.query.type) {
            case MODELS.dims: //'dims-metrics':
               dimKeys = this.getDims();
               metricKeys = this.query.metrics.map(m => m.label);
               break;
            case MODELS.dimsDim: //'dims-dim':
               dimKeys = this.query.groupByDims;
               metricKeys = this.query.pivotDimIn;
               break;
            case MODELS.date: //'date-metrics':
               dimKeys = this.query.dateParts;
               metricKeys = this.query.metrics.map(m => m.label);
               break;
            case MODELS.dateDate:   //'date-date':
               this.chartData = this.prepareChartData4DateDate();
               return;
            case MODELS.dateDim: //'date-dim':
               dimKeys = this.query.dateParts;
               metricKeys = this.query.pivotDimIn;
               break;
            case MODELS.histogram:  //'histogram-metrics':
               dimKeys = [this.query.histogramLable];
               metricKeys = this.query.metrics.map(m => m.label);
               break;
            default:
               alert(`Error in prepareChartData(): Unsupported model: ${this.query.type}`);
               break;
         }

         if (this.resultType === 'GeoChart') {
            this.chartData = this.prepareGeoChartData(dimKeys, metricKeys[0]);
         } else {
         const header = ['Label', ...metricKeys];
            const rows = this.queryResult.map(item => {
               const label = dimKeys.map(key => item[key]).join('-');
               const metrics = metricKeys.map(key => item[key] ?? 0);
               return [label, ...metrics];
            });

            // console.warn('chartData=' + JSON.stringify([header, ...rows]));
            this.chartData = [header, ...rows];
         }
      },

      prepareChartData4DateDate() {
         const xKey = this.query.dateParts[0];
         const header = Object.keys(this.queryResult[0]);
         const seriesKeys = header.filter(key => key !== xKey);
         const rows = this.queryResult.map(item => {
            const xVal = String(item[xKey]);
            const values = seriesKeys.map(key => item[key] ?? 0);
            return [xVal, ...values];
         });

         return [header, ...rows];
      },

      prepareGeoChartData(dimKeys, metricKey) {
         let chartData = [["Location", metricKey]];
         for (const row of this.queryResult) {
            const location = dimKeys.map(field => row[field]).filter(Boolean).join("-");  // was /
            chartData.push([location, row[metricKey]]);
         }

         // console.warn('chartData=' + JSON.stringify(chartData));
         return chartData;
      },

      exportDataToCsvClicked() {
         exportDataToCSV(
            this.queryResultHeaders.map(h => h.text),
            this.queryResult,
            `${this.query.type}.csv`
         );
      },

      getDims() {
         return this.query.dims.map(d => {
            const parts = d.split(' as ');
            return parts[parts.length - 1];
         });
      }
   },

   created() {
      this.init();
      this.modelChanged();
   }
}
</script>
